import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  Plus,
  MapPin,
  Power,
  Edit,
  Trash2
} from 'react-feather';

import './Restaurants.css';
import AddRestaurantNew from "../../../core/modals/addrestaurant/AddRestaurantNew";
import SessionControl from "../../components/SessionControl/sessioncontrol";
import { all_routes } from "../../../Router/all_routes";
import { base_api_url, SKIP_SUBSCRIPTION_CHECK } from "../../../environment.jsx";

import ListRestaurant from '../../components/restaurants/listrestaurant';

const MySwal = withReactContent(Swal);

/**
 * Page de gestion des restaurants de l'utilisateur.
 * Permet d'afficher la liste, d'en ajouter et d'en sélectionner un comme actif.
 */
const Restaurants = () => {
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const route = all_routes;

  // États du composant
  const [restaurants, setRestaurants] = useState([]);
  // const [searchTerm, setSearchTerm] = useState('');
  const [reload, setReload] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [restaurantToEdit, setRestaurantToEdit] = useState(null);
  const [shouldOpenCreateModal, setShouldOpenCreateModal] = useState(false);

  /**
   * Récupère la liste des restaurants via l'API.
   */
  const fetchRestaurants = useCallback(async () => {
    setLoading(true);
    const token = JSON.parse(localStorage.getItem("user"))?.access_token;
    try {
      const response = await axios.get(`${base_api_url}api/restaurant`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 || response.status === 201) {
        const data = response.data.restaurant || [];
        setRestaurants(data);

        if (data.length === 0) {
          MySwal.fire({
            icon: "info",
            // title: "Aucun restaurant",
            text: "Commencez par créer votre premier établissement.",
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
            timer: 3000,
            customClass: { popup: "pixel-toast" }
          });
        }
        setError(null);
      }
    } catch (error) {
      console.error("Error fetching restaurants:", error);

      //       let errorTitle = "Erreur de chargement";
      let errorMessage = "Impossible de charger les restaurants.";

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        switch (status) {
          case 401:
            //             errorTitle = "Session expirée";
            errorMessage = "Votre session a expiré. Vous allez être redirigé vers la page de connexion.";
            setTimeout(() => navigate(route.signin), 2000);
            break;
          case 403:
            //             errorTitle = "Accès refusé";
            errorMessage = "Vous n'avez pas les permissions nécessaires pour accéder à vos restaurants.";
            break;
          case 404:
            //             errorTitle = "Ressource introuvable";
            errorMessage = "Le service de gestion des restaurants est introuvable.";
            break;
          case 429:
            //             errorTitle = "Trop de tentatives";
            errorMessage = "Trop de demandes en peu de temps. Veuillez patienter quelques instants.";
            break;
          case 500:
            //             errorTitle = "Erreur du serveur";
            errorMessage = "Une erreur s'est produite sur le serveur. Veuillez actualiser la page.";
            break;
          case 503:
            //             errorTitle = "Service indisponible";
            errorMessage = "Le service est temporairement indisponible. Veuillez réessayer plus tard.";
            break;
          default:
            //             errorTitle = `Erreur ${status}`;
            errorMessage = errorData?.message || "Une erreur inattendue s'est produite.";
        }
      } else if (error.request) {
        //         errorTitle = "Problème de connexion";
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
      }

      setError(errorMessage);
      setRestaurants([]);

      MySwal.fire({
        icon: "error",
        // title: errorTitle,
        text: errorMessage,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: { popup: "pixel-toast" }
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, route.signin]);

  // Effect pour ouvrir le modal de création quand shouldOpenCreateModal est true
  useEffect(() => {
    if (!shouldOpenCreateModal || !modalRef.current) return;
    const timer = setTimeout(() => {
      if (window.bootstrap?.Modal) {
        window.bootstrap.Modal.getOrCreateInstance(modalRef.current).show();
      }
      setShouldOpenCreateModal(false);
      window.history.replaceState({}, '', location.pathname);
    }, 200);
    return () => clearTimeout(timer);
  }, [shouldOpenCreateModal, location.pathname]);

  // Charger les restaurants au montage ou lors d'un reload
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.restoId) {
      setSelectedRestaurantId(user.restoId);
    }
  }, []);

  // Charger les restaurants au montage ou lors d'un reload
  useEffect(() => {
    fetchRestaurants();
  }, [reload, fetchRestaurants]);

  // Gestion du paramètre URL "select=true" et "create=true"
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('select') === 'true') {
      setShowSelectionModal(true);
    }
    // Gestion du paramètre "create=true" pour ouvrir le modal de création
    if (searchParams.get('create') === 'true') {
      setRestaurantToEdit(null);
      setShouldOpenCreateModal(true);
    }
  }, [location.search]);

  /**
   * Sélectionne un restaurant comme "actif" pour la session actuelle.
   */
  const handleRestaurantSelect = async (restaurantId) => {
    setSelectedRestaurantId(restaurantId);
    localStorage.setItem("selectedRestaurantId", restaurantId.toString());

    try {
      const gotResto = await axios.get(`${base_api_url}api/restaurant/${restaurantId}`);
      localStorage.setItem("restaurant", JSON.stringify(gotResto.data));

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      user.restoId = restaurantId;
      localStorage.setItem("user", JSON.stringify(user));

      window.dispatchEvent(new Event("restaurantChanged"));

      if (showSelectionModal) {
        setShowSelectionModal(false);
        MySwal.fire({
          icon: "success",
          // title: "Restaurant sélectionné !",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2000,
          customClass: { popup: "pixel-toast" }
        });
        setTimeout(() => navigate(route.articlesNew), 1500);
      }
    } catch (err) {
      console.error("Erreur lors de la sélection :", err);
    }
  };

 const handleDeleteRestaurant = async (restaurantId) => {
  const token = JSON.parse(localStorage.getItem("user"))?.access_token;
  
  // Demander confirmation avant la suppression
  const result = await MySwal.fire({
     title: 'Êtes-vous sûr ?',
    text: "Voulez-vous vraiment supprime ? Cette action est irréversible !.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ff6a3d',
    cancelButtonColor: '#94a3b8',
    confirmButtonText: 'Oui, supprimer',
    cancelButtonText: 'Annuler'
  });

  if (!result.isConfirmed) {
    return;
  }

  setLoading(true);
  
  try {
    // Requête PUT avec seulement l'ID dans l'URL, pas de body
    const response = await axios.put(
      `${base_api_url}api/statut_restaurant/${restaurantId}`,
      {}, 
      {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      }
    );

    // Vérifier si la requête a réussi
    if (response.status === 200 || response.status === 201) {
      MySwal.fire({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        icon: "success",
        title: "Restaurant supprimer avec success",
        timer: 2000,
        text: "Le restaurant a été marqué comme supprimé avec succès."
      });

      // Mettre à jour l'état local pour retirer le restaurant de la liste
      setRestaurants(prevRestaurants => 
        prevRestaurants.filter(restaurant => restaurant.id_restaurant !== restaurantId)
      );

      // Si le restaurant désactivé était le restaurant sélectionné, réinitialiser la sélection
      if (selectedRestaurantId === restaurantId) {
        setSelectedRestaurantId(null);
        localStorage.removeItem("selectedRestaurantId");
        
        // Supprimer le restaurant du localStorage
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user.restoId === restaurantId) {
          delete user.restoId;
          localStorage.setItem("user", JSON.stringify(user));
        }
        
        // Notifier le changement
        window.dispatchEvent(new Event("restaurantChanged"));
      }
    }
  } catch (error) {
    MySwal.fire({
      icon: "error",
      toast: true,
      position: "bottom-end",
      title: "Échec de la suppression",
      timer: 5000,
      timerProgressBar: true
    });
  } finally {
    setLoading(false);
  }
};


  const effectOnModal = () => setReload(!reload);

  /**
   * Prépare les données pour l'édition d'un restaurant
   */
  const handleEditRestaurant = (restaurant) => {
    console.log('Editing restaurant:', restaurant); // Debug
    setRestaurantToEdit(restaurant);
  };

  /**
   * Ferme le modal d'édition
   */
  const handleCloseEditModal = () => {
    setRestaurantToEdit(null);
  };

  /**
   * Génère les initiales à partir du nom du restaurant.
   */
  const getInitials = (name) => {
    if (!name) return "RS";
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleOpenCreateModal = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;
      const id_utilisateur = JSON.parse(localStorage.getItem("user"))?.id_utilisateur;
      
      const subResponse = await axios.get(`${base_api_url}/abonnement/abonnement/${id_utilisateur}`, { headers: { Authorization: `Bearer ${token}` } });
      const subs = subResponse.data;
      let activeSubscription = null;
      const now = new Date();

      if (Array.isArray(subs)) {
          activeSubscription = subs.filter(sub => new Date(sub.date_debut) <= now && new Date(sub.date_fin) >= now)
              .sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut))[0];
      } else if (subs && subs.id_abonnement) {
          // Check if the single subscription object is active
          const dateDebut = new Date(subs.date_debut);
          const dateFin = new Date(subs.date_fin);
          if (dateDebut <= now && dateFin >= now) {
              activeSubscription = subs;
          }
      } else if (subs && subs.abonnements && Array.isArray(subs.abonnements)) {
          activeSubscription = subs.abonnements.filter(sub => new Date(sub.date_debut) <= now && new Date(sub.date_fin) >= now)[0];
      }

      // Vérifier si aucun abonnement actif n'est trouvé
      if (!activeSubscription && !SKIP_SUBSCRIPTION_CHECK) {
          MySwal.fire({
              title: 'Aucun abonnement actif',
              text: "Vous ne possédez aucun abonnement actif. Vous devez souscrire à un forfait pour pouvoir ajouter un nouvel établissement.",
              icon: 'info',
              showCancelButton: true,
              confirmButtonColor: '#ff5722',
              cancelButtonColor: '#94a3b8',
              confirmButtonText: 'Souscrire maintenant',
              cancelButtonText: 'Plus tard'
          }).then((result) => {
              if (result.isConfirmed) {
                  navigate(route.settings);
              }
          });
          setLoading(false);
          return;
      }


      // 4. Lecture dynamique de la limite depuis la configuration de l'abonnement
      let limitMax = null; 
      let planName = activeSubscription?.nom_abonnement || activeSubscription?.nom || "votre forfait actuel";

      if (activeSubscription) {
          // Extraire l'objet configuration s'il existe (généralement JSON)
          const config = activeSubscription.configuration_abonnement || activeSubscription.configuration || activeSubscription;

          // Propriétés possibles renvoyées par l'API pour la limite de restaurants
          const possibleProps = ["limite_restaurant", "max_restaurants", "limite_restaurants", "nb_restaurant_max", "max_restaurant", "limite"];

          for (const prop of possibleProps) {
              if (config && config[prop] !== undefined && config[prop] !== null) {
                  limitMax = config[prop];
                  break;
              }
          }

          // Fallback sur le type d'abonnement si aucune propriété claire n'est trouvée dans la config
          if (limitMax === null || limitMax === undefined) {
              const type = parseInt(activeSubscription.type_abonnement || activeSubscription.type, 10);
              switch (type) {
                  case 1: limitMax = 1; break; // Starter / Standard (1 restaut selon l'utilisateur)
                  case 2: limitMax = 3; break; // Planificateur
                  case 3: limitMax = Infinity; break; // Premium (Illimité)
                  default: 
                    console.log("Unknown subscription type:", type);
                    limitMax = 1; // Sécurité par défaut à 1 au lieu de Infinity
                    break;
              }
          } else {
              // Gestion des mots-clés typiques pour les forfaits illimités (-1, "unlimited")
              if (limitMax === -1 || String(limitMax).toLowerCase().includes("unlimit") || String(limitMax).toLowerCase().includes("illimit")) {
                  limitMax = Infinity;
              } else {
                  limitMax = parseInt(limitMax, 10);
              }
          }
      } else {
          limitMax = 1; // Sécurité si aucun abonnement n'est défini
      }

      console.log("Check limit:", { 
          restaurantsCount: restaurants.length, 
          limitMax, 
          planName, 
          activeSub: activeSubscription 
      });

      // 5. Appliquer le blocage si la limite configurée est atteinte
      if (!SKIP_SUBSCRIPTION_CHECK && limitMax !== Infinity && restaurants.length >= limitMax) {
          console.warn("LIMIT REACHED: blocking opening modal");
          MySwal.fire({
              icon: "warning",
              title: "Limite atteinte",
              text: `Votre abonnement ${planName} vous limite à ${limitMax} établissement(s). Veuillez mettre à niveau votre forfait pour en ajouter d'autres.`,
              confirmButtonText: "Gérer l'abonnement",
              showCancelButton: true,
              cancelButtonText: "Plus tard",
              confirmButtonColor: '#ff5722',
          }).then((result) => {
              if (result.isConfirmed) {
                  navigate(route.settings);
              }
          });
          setLoading(false);
          return;
      }

      
      setRestaurantToEdit(null);
      if (window.bootstrap?.Modal && modalRef.current) {
        window.bootstrap.Modal.getOrCreateInstance(modalRef.current).show();
      }
    } catch (err) {
      console.error(err);
      setRestaurantToEdit(null);
      if (window.bootstrap?.Modal && modalRef.current) {
        window.bootstrap.Modal.getOrCreateInstance(modalRef.current).show();
      }
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des restaurants selon la recherche
  const filteredRestaurants = restaurants.filter(r =>
    r.nom?.toLowerCase().includes(r.nom.toLowerCase())
  );

  if (error) {
    return <div className="p-5 text-danger">Erreur : {error}</div>;
  }

  return (
    <div className="page-wrapper bg-white">
      <SessionControl storageKey="user" propsup="email" redirectTo={route.signin} />

      <div className="restaurants-page">
        {/* EN-TÊTE */}
        <div className="restaurants-header">
          <div className="header-left">
            <h2>Restaurants</h2>
            <p>Gérez vos établissements.</p>
          </div>

          {/* BOUTON D'AJOUT RESTAURANT */}
          <button
            className="btn-add-restaurant"
            onClick={handleOpenCreateModal}
            title="Ajouter un nouveau restaurant"
          >
            <Plus size={18} className="d-md-none" />
            <span className="d-none d-md-inline">Ajouter un restaurant</span>
          </button>
          {/* <button
            className="btn-add-restaurant"
            data-bs-toggle="modal"
            data-bs-target="#new-restau"
            onClick={() => setRestaurantToEdit(null)}
          >
            <Plus size={18} className="d-md-none" />
            <span className="d-none d-md-inline">Ajouter un restaurant</span>
          </button> */}
        </div>

        {/* BARRE DE RECHERCHE */}
        {/* <div className="search-container">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher un restaurant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div> */}

        {/* GRILLE DES RESTAURANTS */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        ) : (
          <div className="restaurants-grid">
            {filteredRestaurants.map((restaurant) => {
              const isSelected = selectedRestaurantId === restaurant.id_restaurant;
              return (
                <div
                  key={restaurant.id_restaurant}
                  className={`restaurant-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => isSelected ? navigate(route.articlesNew) : handleRestaurantSelect(restaurant.id_restaurant)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="card-top">
                    <div className="restaurant-avatar-box">
                      {getInitials(restaurant.nom)}
                    </div>
                    <div className="card-actions">
                      <button
                        className={`action-icon ${isSelected ? 'toggle-active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isSelected) handleRestaurantSelect(restaurant.id_restaurant);
                        }}
                        title={isSelected ? "Restaurant actif" : "Définir comme actif"}
                      >
                        <Power />
                      </button>
                      <button
                        className="action-icon"
                        data-bs-toggle="modal"
                        data-bs-target="#new-restau"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditRestaurant(restaurant);
                        }}
                        title="Modifier"
                      >
                        <Edit />
                      </button>
                      <button
                        className="action-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRestaurant(restaurant.id_restaurant);
                        }}
                        title="Supprimer"
                      >
                        <Trash2 />
                      </button>
                    </div>
                  </div>

                  <div className="restaurant-info">
                    <h3>{restaurant.nom}</h3>
                    <div className="restaurant-location">
                      <MapPin size={14} />
                      <span>{restaurant.adresse || "Localisation non définie"}</span>
                    </div>
                  </div>

                  <div className="card-divider"></div>

                  <div className={`status-indicator ${isSelected ? 'active' : 'inactive'}`}>
                    <span className="status-dot"></span>
                    <span>{isSelected ? 'ACTIF' : 'INACTIF'}</span>
                  </div>
                </div>
              );
            })}

            {/* CARD AJOUTER UN RESTAURANT */}
            <div
              className="add-new-card"
              onClick={handleOpenCreateModal}
            >
              <Plus size={32} className="plus-icon" />
              <span>Nouvel établissement</span>
            </div>
          </div>
        )}

        {/* MODAL D'AJOUT/MODIFICATION */}
        <AddRestaurantNew
          ref={modalRef}
          onFormSubmit={() => {
            effectOnModal();
            handleCloseEditModal();
          }}
          restaurantToEdit={restaurantToEdit}
        />

        {/* MODAL DE SÉLECTION INITIALE */}
        {showSelectionModal && restaurants.length > 0 && (
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
            <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex={-1}>
              <div className="modal-dialog modal-lg modal-dialog-centered mx-auto">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                  <div className="modal-header border-0 pb-0">
                    <h5 className="modal-title fw-bold">Sélectionner un établissement</h5>
                  </div>
                  <div className="modal-body p-4">
                    <p className="text-muted mb-4">Choisissez le restaurant que vous souhaitez administrer pour cette session.</p>
                    <div className="row g-3">
                      {restaurants.map((restaurant) => (
                        <div key={restaurant.id_restaurant} className="col-md-6">
                          <ListRestaurant
                            title={restaurant.nom}
                            id={restaurant.id_restaurant}
                            isSelected={selectedRestaurantId === restaurant.id_restaurant}
                            onSelect={handleRestaurantSelect}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Restaurants;
